import { AuthContext } from "@providers/AuthProvider";
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import Header from "@components/Header/Header"
import SideMenu from "@components/SideMenu/SideMenu";
import Localization from "@localization/Index";
import HelperContent from "@helpercontents/Localization";
import { DEFAULT_LANG } from "@utils/Constants";
import { ModuleIdEnum } from "cs.node.utils/csapi/provisioning";
import { UserRoleEnum } from "cs.node.utils/RoleUtils";
import AccessDenied from "@pages/Errors/AccessDenied";
import { useMobileMediaQuery } from "@utils/MediaComponents";

type Props = {
    children: JSX.Element
    modules?: (keyof typeof ModuleIdEnum)[]
    roles?: (keyof typeof UserRoleEnum)[]
    // the page is available only if packaging is enabled (PrintNode configured on the server)
    packagingRequired?: boolean
}

const Protected = (props:Props):JSX.Element => {
    const auth = React.useContext(AuthContext)
    const lang = auth.userData?.defaultLang ?? DEFAULT_LANG
    const isMobile = useMobileMediaQuery()
    
    const { children, modules, roles, packagingRequired } = props
    const location = useLocation()
    if(auth.userData===null) {
        return <></>
    }
    if(!auth.userData) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to="/login" state={{ from: location }} replace />
    }

    // check if the user has access to at least one of the modules listed in the modules property
    const modulesNotAllowed = modules && !auth.userData.modules?.find(module=>{
        return module._id && modules.includes(module._id) && module.enabled
    })

    // check if the user has the role needed for this page
    const roleNotAllowed = !roles || !roles.includes(auth.userData.role)

    // check if packaging is enabled, if requested by the page
    const packagingNotAllowed = packagingRequired === true && auth.userData.packaging_enabled !== true
    const isAllowed = !modulesNotAllowed && !roleNotAllowed && !packagingNotAllowed
    Localization.setLanguage(lang)
    HelperContent.setLanguage(lang)

    if(isMobile) {
        return <div className="h-100 w-100" style={{minHeight: '100%', paddingTop:'50px'}}>
            <Header />
            <div className="container-fluid h-100">
                {isAllowed ? children : <AccessDenied />}
            </div>
        </div>
    }

    return (
        <div className="h-100" style={{minHeight: '100%', paddingTop:'50px'}}>
            <Header />
            <div className="d-flex h-100" style={{minHeight: '100%', marginTop: '-6px'}}>
                <div className="d-none d-md-block" style={{width: '280px', minWidth: '280px'}}>
                    <SideMenu />
                </div>
                <div className="container-fluid h-100" style={{minHeight: '100%', paddingLeft: 0}}>
                    <div className="h-100" style={{minHeight: '100%', backgroundColor: '#F9F9F9'}}>
                        <div className="h-100" style={{minHeight: '100%', overflowY: 'auto', padding: "35px", backgroundColor: '#fff', borderRadius: '12px', boxShadow: "rgba(0, 0, 0, 0.03) 0px 3px 4px 0px"}}>
                        {isAllowed ? children : <AccessDenied />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Protected